import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { maintenanceService } from '../../../services/maintenanceService';
import { useAuth } from '../../../contexts/AuthContext';

const MaintenanceModeControl = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [message, setMessage] = useState('');
  const [estimatedReturn, setEstimatedReturn] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const status = await maintenanceService?.getMaintenanceStatus();
      setMaintenanceEnabled(status?.enabled || false);
      setMessage(status?.message || '');
      setEstimatedReturn(status?.estimatedReturn || '');
    } catch (err) {
      setError('Failed to load maintenance settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    const newEnabled = !maintenanceEnabled;
    setMaintenanceEnabled(newEnabled);
    await saveSettings(newEnabled);
  };

  const saveSettings = async (enabled = maintenanceEnabled) => {
    setSaving(true);
    setError('');
    setSaveSuccess(false);
    try {
      const { error: saveError } = await maintenanceService?.updateMaintenanceSettings({
        enabled,
        message,
        estimatedReturn,
        updatedBy: user?.email || 'officer'
      });
      if (saveError) {
        setError(saveError?.message || 'Failed to save settings');
        // Revert toggle if save failed
        if (enabled !== maintenanceEnabled) setMaintenanceEnabled(!enabled);
      } else {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      setError('Failed to save maintenance settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 animate-pulse">
        <div className="h-5 bg-muted rounded w-48 mb-4" />
        <div className="h-12 bg-muted rounded mb-3" />
        <div className="h-20 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div className={`bg-card border rounded-xl p-6 transition-all duration-300 ${
      maintenanceEnabled
        ? 'border-amber-500/50 bg-amber-500/5' :'border-border'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            maintenanceEnabled ? 'bg-amber-500/20' : 'bg-muted'
          }`}>
            <Icon
              name="Wrench"
              size={20}
              className={maintenanceEnabled ? 'text-amber-500' : 'text-muted-foreground'}
            />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm">Maintenance Mode</h3>
            <p className="text-xs text-muted-foreground">Control public app access</p>
          </div>
        </div>

        {/* Status badge */}
        <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
          maintenanceEnabled
            ? 'bg-amber-500/20 text-amber-600 border border-amber-500/30' :'bg-green-500/10 text-green-600 border border-green-500/20'
        }`}>
          <div className={`w-1.5 h-1.5 rounded-full ${
            maintenanceEnabled ? 'bg-amber-500 animate-pulse' : 'bg-green-500'
          }`} />
          {maintenanceEnabled ? 'MAINTENANCE ON' : 'APP LIVE'}
        </div>
      </div>
      {/* Toggle switch */}
      <div className="flex items-center justify-between bg-muted/50 rounded-lg p-4 mb-4">
        <div>
          <p className="text-sm font-medium text-foreground">
            {maintenanceEnabled ? 'Maintenance mode is ACTIVE' : 'Maintenance mode is OFF'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {maintenanceEnabled
              ? 'Regular users are seeing the maintenance page' :'All users can access the app normally'
            }
          </p>
        </div>
        <button
          onClick={handleToggle}
          disabled={saving}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 ${
            maintenanceEnabled
              ? 'bg-amber-500 focus:ring-amber-500' :'bg-muted-foreground/30 focus:ring-primary'
          }`}
          aria-label="Toggle maintenance mode"
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
              maintenanceEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      {/* Message input */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-muted-foreground mb-1.5">
          Maintenance Message
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e?.target?.value)}
          placeholder="We are currently performing scheduled maintenance. We'll be back shortly."
          rows={3}
          className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
        />
      </div>
      {/* Estimated return */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-muted-foreground mb-1.5">
          Estimated Return Time <span className="text-muted-foreground/60">(optional)</span>
        </label>
        <input
          type="text"
          value={estimatedReturn}
          onChange={(e) => setEstimatedReturn(e?.target?.value)}
          placeholder="e.g. Today at 3:00 PM UTC, or Within 2 hours"
          className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
      </div>
      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-destructive text-xs bg-destructive/10 rounded-lg px-3 py-2 mb-3">
          <Icon name="AlertCircle" size={14} />
          {error}
        </div>
      )}
      {/* Success */}
      {saveSuccess && (
        <div className="flex items-center gap-2 text-green-600 text-xs bg-green-500/10 rounded-lg px-3 py-2 mb-3">
          <Icon name="CheckCircle" size={14} />
          Settings saved successfully
        </div>
      )}
      {/* Save button */}
      <button
        onClick={() => saveSettings()}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-primary-foreground text-sm font-medium py-2.5 rounded-lg transition-colors duration-200"
      >
        {saving ? (
          <><Icon name="Loader2" size={15} className="animate-spin" /> Saving...</>
        ) : (
          <><Icon name="Save" size={15} /> Save Settings</>
        )}
      </button>
      {/* Officer bypass note */}
      <p className="text-xs text-muted-foreground/60 text-center mt-3">
        <Icon name="Info" size={11} className="inline mr-1" />
        Officers always bypass maintenance mode and access the app normally
      </p>
    </div>
  );
};

export default MaintenanceModeControl;
